import React, { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../hooks/useUrlState.ts';
import { projectAPI } from '../services/api.ts';
import { Layout } from '../components/Layout.tsx';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  ColorSwatch,
  Divider,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import {
  Ban,
  CircleCheck,
  Eye,
  FolderKanban,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { ProjectForm } from '../components/ProjectForm.tsx';
import {
  ConfirmModal,
  EmptyState,
  PageHeader,
  TablePagination,
} from '../components/app/index.ts';
import { toast } from '../lib/toast.ts';

export const Projects: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<Partial<any> | null>(
    null,
  );
  // Kept in the address, so the list comes back as it was left.
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [totalItems, setTotalItems] = useState(0);

  const [loadingStatus, setLoadingStatus] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    projectId: string | null;
  }>({
    isOpen: false,
    projectId: null,
  });
  const [statusConfirmation, setStatusConfirmation] = useState<{
    isOpen: boolean;
    projectId: number | null;
    currentStatus: number;
  }>({
    isOpen: false,
    projectId: null,
    currentStatus: 0,
  });
  const [searchTerm, setSearchTerm] = useUrlState('search', '', {
    resetPage: true,
  });
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [statusFilter, setStatusFilter] = useUrlState('status', '', {
    resetPage: true,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await projectAPI.getAll(page, 10, {
        search: debouncedSearch,
        status: statusFilter,
      });
      if (response.data) {
        setProjects(response.data.projects);
        setTotalPages(response.data.pagination.pages);
        setTotalItems(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSubmit = async (projectData: any) => {
    try {
      const projectId = currentProject?.id;

      let response;
      if (projectId) {
        // Update existing project
        response = await projectAPI.update(projectId, projectData);
      } else {
        // Create new project
        response = await projectAPI.create(projectData);
      }

      if (response.success) {
        toast.success(response.message);
        setIsModalOpen(false);
        fetchProjects();
      } else {
        // throw new Error(response.message || 'Operation failed');
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || error.message || 'Operation failed',
      );
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setError('');
      setSuccessMessage('');

      const response = await projectAPI.delete(id);

      if (response.success) {
        setSuccessMessage(response.message);
        fetchProjects();
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
    } finally {
      setDeleteConfirmation({ isOpen: false, projectId: null });
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      setLoadingStatus(id);
      setError('');
      const response = await projectAPI.toggleStatus(id);
      if (response.success) {
        toast.success(response.message);
        fetchProjects();
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingStatus(null);
    }
  };

  // Keeps the status so the wording does not flip while the modal fades out.
  const closeStatusConfirmation = () =>
    setStatusConfirmation((current) => ({
      ...current,
      isOpen: false,
      projectId: null,
    }));

  const detail = (label: string, value: React.ReactNode) => (
    <Text size="sm">
      <Text span fw={600}>
        {label}:
      </Text>{' '}
      {value}
    </Text>
  );

  return (
    <Layout>
      <PageHeader
        title="Projects"
        description="Manage your project portfolio"
        actions={
          <Button
            leftSection={<Plus size={16} />}
            onClick={() => {
              setCurrentProject({});
              setIsModalOpen(true);
            }}
          >
            Add Project
          </Button>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              type="search"
              placeholder="Search by project name or ID..."
              leftSection={<Search size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              flex={1}
              miw={220}
            />
            <Select
              aria-label="Status"
              placeholder="All statuses"
              data={[
                { value: '1', label: 'Active' },
                { value: '0', label: 'Inactive' },
              ]}
              value={statusFilter || null}
              onChange={(value) => setStatusFilter(value || '')}
              clearable
              w={{ base: '100%', sm: 180 }}
            />
            {(searchTerm || statusFilter) && (
              <Button
                color="red"
                variant="light"
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearch('');
                  setStatusFilter('');
                }}
              >
                Reset
              </Button>
            )}
          </Group>

          {successMessage && (
            <Alert
              color="green"
              variant="light"
              withCloseButton
              onClose={() => setSuccessMessage('')}
            >
              {successMessage}
            </Alert>
          )}

          {error && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : projects.length === 0 ? (
            <EmptyState
              icon={<FolderKanban size={28} />}
              title="No projects"
              description="Get started by adding a new project."
            />
          ) : (
            <>
              <Table.ScrollContainer minWidth={700}>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Logo</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Company</Table.Th>
                      <Table.Th>
                        <VisuallyHidden>Actions</VisuallyHidden>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projects.map((project) => (
                      <Table.Tr key={project._id}>
                        <Table.Td>
                          {project.logo ? (
                            <Image
                              src={project.logo}
                              fallbackSrc="/suncrop-logo.png"
                              alt={`${project.name} logo`}
                              w={40}
                              h={40}
                              fit="contain"
                            />
                          ) : (
                            <Text size="xs" c="dimmed">
                              No logo
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>{project.name}</Table.Td>
                        <Table.Td>
                          <Badge
                            variant="light"
                            color={project.status === 1 ? 'green' : 'red'}
                          >
                            {project.status === 1 ? 'Active' : 'Inactive'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{project.company?.name || 'N/A'}</Table.Td>
                        <Table.Td>
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            <ActionIcon
                              variant="subtle"
                              aria-label="View project"
                              onClick={() => {
                                setSelectedProject(project);
                                setIsViewModalOpen(true);
                              }}
                            >
                              <Eye size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              aria-label="Edit project"
                              onClick={async () => {
                                try {
                                  const response = await projectAPI.getById(
                                    project.id,
                                  );
                                  if (response.success) {
                                    setCurrentProject({
                                      ...response.data,
                                      odoo_configuration_id: '',
                                      company_id:
                                        response.data?.company_id || '',
                                    });
                                    setIsModalOpen(true);
                                  }
                                } catch (error: any) {
                                  toast.error(error.message);
                                }
                              }}
                            >
                              <Pencil size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color={project.status === 1 ? 'red' : 'green'}
                              aria-label={
                                project.status === 1
                                  ? 'Deactivate project'
                                  : 'Activate project'
                              }
                              disabled={loadingStatus === project.id}
                              onClick={() =>
                                setStatusConfirmation({
                                  isOpen: true,
                                  projectId: project.id,
                                  currentStatus: project.status,
                                })
                              }
                            >
                              {project.status === 1 ? (
                                <Ban size={18} />
                              ) : (
                                <CircleCheck size={18} />
                              )}
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label="Delete project"
                              onClick={() =>
                                setDeleteConfirmation({
                                  isOpen: true,
                                  projectId: project.id,
                                })
                              }
                            >
                              <Trash2 size={18} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={totalItems}
                pageSize={10}
              />
            </>
          )}
        </Stack>
      </Paper>

      {/* Project Modal */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <Text fw={600}>
            {currentProject?._id ? 'Edit Project' : 'New Project'}
          </Text>
        }
        size="xl"
      >
        <ProjectForm
          initialValues={{
            _id: currentProject?._id,
            name: currentProject?.name || '',
            status: currentProject?.status || 1,
            company_id: currentProject?.company?.id || '',
            logo: currentProject?.logo || '',
            color_scheme: {
              primary: currentProject?.color_scheme?.primary || '',
              secondary: currentProject?.color_scheme?.secondary || '',
            },
            odoo: {
              url: currentProject?.odoo?.url || '',
              db: currentProject?.odoo?.db || '',
              username: currentProject?.odoo?.username || '',
              password: currentProject?.odoo?.password || '',
            },
            admin_email: currentProject?.admin_user?.email || '',
            heading: currentProject?.heading || '',
            sub_heading: currentProject?.sub_heading || '',
            audit_department_email:
              currentProject?.audit_department_email || '',
          }}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      {/* View Project Details Modal */}
      <Modal
        opened={isViewModalOpen && !!selectedProject}
        onClose={() => setIsViewModalOpen(false)}
        title={<Text fw={600}>Project Details</Text>}
      >
        {selectedProject && (
          <Stack gap="md">
            <Group justify="center">
              <Image
                src={selectedProject.logo || '/suncrop-logo.png'}
                fallbackSrc="/suncrop-logo.png"
                alt={
                  selectedProject.logo
                    ? `${selectedProject.name} logo`
                    : 'Default logo'
                }
                w={96}
                h={96}
                fit="contain"
              />
            </Group>

            <Stack gap={4}>
              {detail('Name', selectedProject.name)}
              {detail(
                'Status',
                selectedProject.status === 1 ? 'Active' : 'Inactive',
              )}
              {detail('Admin Email', selectedProject.admin_user?.email || 'N/A')}
              {detail('Company', selectedProject.company?.name || 'N/A')}
              {detail('Heading', selectedProject.heading || 'N/A')}
              {detail('Sub Heading', selectedProject.sub_heading || 'N/A')}
            </Stack>

            <Divider />

            <Stack gap={4}>
              <Text fw={600}>Odoo Configuration:</Text>
              {detail('URL', selectedProject.odoo?.url || 'N/A')}
              {detail('Database', selectedProject.odoo?.db || 'N/A')}
              {detail('Username', selectedProject.odoo?.username || 'N/A')}
            </Stack>

            <Divider />

            <Stack gap="xs">
              <Text fw={600}>Color Scheme:</Text>
              <SimpleGrid cols={2}>
                <Stack gap={4}>
                  <Text size="sm">Primary:</Text>
                  <Group gap="xs">
                    <ColorSwatch
                      size={24}
                      radius="sm"
                      color={
                        selectedProject.color_scheme?.primary || 'transparent'
                      }
                    />
                    <Text size="sm">
                      {selectedProject.color_scheme?.primary || 'N/A'}
                    </Text>
                  </Group>
                </Stack>
                <Stack gap={4}>
                  <Text size="sm">Secondary:</Text>
                  <Group gap="xs">
                    <ColorSwatch
                      size={24}
                      radius="sm"
                      color={
                        selectedProject.color_scheme?.secondary || 'transparent'
                      }
                    />
                    <Text size="sm">
                      {selectedProject.color_scheme?.secondary || 'N/A'}
                    </Text>
                  </Group>
                </Stack>
              </SimpleGrid>
            </Stack>

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setIsViewModalOpen(false)}>
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <ConfirmModal
        opened={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, projectId: null })}
        onConfirm={() => {
          if (deleteConfirmation.projectId) {
            handleDelete(deleteConfirmation.projectId);
          }
        }}
        title="Delete Project"
        confirmLabel="Delete"
      >
        Are you sure you want to delete this project? This action cannot be
        undone.
      </ConfirmModal>

      <ConfirmModal
        opened={statusConfirmation.isOpen}
        onClose={closeStatusConfirmation}
        onConfirm={() => {
          if (statusConfirmation.projectId) {
            handleToggleStatus(statusConfirmation.projectId);
            closeStatusConfirmation();
          }
        }}
        title={`${
          statusConfirmation.currentStatus === 1 ? 'Deactivate' : 'Activate'
        } Project`}
        confirmLabel={
          statusConfirmation.currentStatus === 1 ? 'Deactivate' : 'Activate'
        }
        color={statusConfirmation.currentStatus === 1 ? 'red' : 'green'}
      >
        Are you sure you want to{' '}
        {statusConfirmation.currentStatus === 1 ? 'deactivate' : 'activate'}{' '}
        this project?
        {statusConfirmation.currentStatus === 1
          ? ' This will prevent users from accessing the project.'
          : ' This will allow users to access the project.'}
      </ConfirmModal>
    </Layout>
  );
};
